/**
 * THE PLAN ON SCREEN, AS TEXT FOR THE NEXT TURN'S HISTORY.
 *
 * The chat clients send history as text only, and a plan lives in the
 * itinerary object, not in the text: on a multi-city trip the text is one
 * opening sentence. So the server never saw the plan the guest was editing,
 * and "swap day 2 and 3" rebuilt the trip from nothing (2026-11-01 itinerary
 * tests). Appending this to an assistant turn's history content gives the
 * server the plan back. It is history only and never shown.
 *
 * "Day N" stays English on purpose: the server reads these headings in every
 * language, and English is the one it is sure of.
 */

export interface PlanHistorySlot {
  time: string;
  matchSlug?: string;
  place?: { title: string; note?: string } | null;
  note?: string;
}

export interface PlanHistoryDay {
  label?: string;
  city?: string;
  slots: PlanHistorySlot[];
}

export interface PlanHistoryMatch {
  slug: string;
  title?: string | null;
}

/** Room the plan may take inside one history turn. */
const PLAN_CHARS = 2400;

export function planAsHistoryText(
  days: ReadonlyArray<PlanHistoryDay> | null | undefined,
  matches: ReadonlyArray<PlanHistoryMatch> = [],
): string {
  if (!days || days.length === 0) return "";
  const titles = new Map(matches.map((m) => [m.slug, m.title ?? m.slug]));
  const lines = days.map((d, i) => {
    const stops = d.slots
      .map((s) => {
        const name = s.matchSlug ? titles.get(s.matchSlug) ?? s.matchSlug : s.place?.title;
        return name ? `${s.time}: ${name}` : null;
      })
      .filter((x): x is string => !!x);
    return `Day ${i + 1}${d.city ? ` (${d.city})` : ""}: ${stops.join("; ")}`;
  });
  const out = `The plan shown:\n${lines.join("\n")}`;
  return out.length <= PLAN_CHARS ? out : out.slice(0, PLAN_CHARS);
}

/**
 * One assistant turn's history content: its text, then the plan it showed,
 * fitted inside `max` characters with the plan kept whole.
 */
export function assistantHistoryContent(
  text: string,
  days: ReadonlyArray<PlanHistoryDay> | null | undefined,
  matches: ReadonlyArray<PlanHistoryMatch> = [],
  max = 4000,
): string {
  const plan = planAsHistoryText(days, matches);
  if (!plan) return text.slice(0, max);
  const room = Math.max(0, max - plan.length - 2);
  const head = text.slice(0, room).trim();
  return head ? `${head}\n\n${plan}` : plan;
}
